export function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    // Check if we're in StackBlitz environment
    const isStackBlitz = window.location.hostname.includes('stackblitz') || 
                        window.location.hostname.includes('webcontainer');
    
    if (!isStackBlitz) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then(registration => {
            console.log('Service Worker registered with scope:', registration.scope);
            
            // Listen for updates
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    // New service worker is available
                    showUpdateNotification(registration);
                  }
                });
              }
            });
            
            // Check for waiting service worker
            if (registration.waiting) {
              showUpdateNotification(registration);
            }
          })
          .catch(error => {
            console.error('Service Worker registration failed:', error);
          });
        
        // Listen for controller change to auto-reload
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          // Only reload if we're expecting a controller change (after an update)
          if (refreshing) return;
          
          // Check if this is actually a new service worker taking control
          navigator.serviceWorker.getRegistration().then(registration => {
            if (registration?.waiting) {
              // There's a waiting service worker, this is a legitimate update
              refreshing = true;
              console.log('New service worker took control, reloading...');
              window.location.reload();
            }
          });
        });
      });
    } else {
      console.log('Service Worker registration skipped in StackBlitz environment');
    }
  }
}

// Function to show update notification
function showUpdateNotification(registration: ServiceWorkerRegistration) {
  // Prevent multiple notifications
  if (document.getElementById('sw-update-notification')) {
    return;
  }
  
  // Create update notification element
  const updateNotification = document.createElement('div');
  updateNotification.id = 'sw-update-notification';
  updateNotification.className = 'fixed top-4 right-4 bg-yellow-500 text-black px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-3';
  updateNotification.innerHTML = `
    <div>
      <p class="font-semibold">New version available!</p>
      <p class="text-sm">Click to update the app</p>
    </div>
    <button id="sw-update-button" class="bg-black text-yellow-400 px-4 py-2 rounded font-medium hover:bg-gray-800 transition-colors">
      Update
    </button>
    <button id="sw-dismiss-button" class="text-black hover:text-gray-700 transition-colors">
      ✕
    </button>
  `;
  
  document.body.appendChild(updateNotification);
  
  // Handle update button click
  const updateButton = document.getElementById('sw-update-button');
  const dismissButton = document.getElementById('sw-dismiss-button');
  
  updateButton?.addEventListener('click', () => {
    // Tell the waiting service worker to skip waiting
    registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
    updateNotification.remove();
  });
  
  dismissButton?.addEventListener('click', () => {
    updateNotification.remove();
  });
  
  // Auto-dismiss after 10 seconds
  setTimeout(() => {
    if (document.getElementById('sw-update-notification')) {
      updateNotification.remove();
    }
  }, 10000);
}

// Function to check if the app can be installed (not already installed and not in standalone mode)
export function checkInstallability() {
  // Create a variable to track if we've shown the install prompt
  let deferredPrompt: any;
  let installPromptShown = false;

  // Listen for the beforeinstallprompt event
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent the default browser install prompt
    e.preventDefault();
    
    // Save the event so it can be triggered later
    deferredPrompt = e;
    
    // Show your custom install button or notification (only once)
    if (!installPromptShown) {
      showInstallPromotion();
      installPromptShown = true;
    }
  });

  // iOS-specific install detection
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
  
  if (isIOS && !isInStandaloneMode && !installPromptShown) {
    // Show iOS-specific install instructions
    showIOSInstallInstructions();
    installPromptShown = true;
  }

  // Function to show your custom install UI
  function showInstallPromotion() {
    // You can implement this based on your UI design
    // For example, show a button or banner that calls installPWA()
    const installButton = document.createElement('button');
    installButton.id = 'install-button';
    installButton.textContent = 'Install App';
    installButton.className = 'fixed bottom-4 right-4 bg-yellow-500 text-black px-4 py-2 rounded-lg shadow-lg z-50 font-medium';
    installButton.addEventListener('click', installPWA);
    
    // Only add if it doesn't already exist
    if (!document.getElementById('install-button')) {
      document.body.appendChild(installButton);
      
      // Auto-hide after 10 seconds
      setTimeout(() => {
        const button = document.getElementById('install-button');
        if (button) {
          button.style.opacity = '0';
          button.style.transform = 'translateY(100px)';
          button.style.transition = 'all 0.3s ease';
          setTimeout(() => button.remove(), 300);
        }
      }, 10000);
    }
  }

  // Function to show iOS-specific install instructions
  function showIOSInstallInstructions() {
    const iosPrompt = document.createElement('div');
    iosPrompt.id = 'ios-install-prompt';
    iosPrompt.innerHTML = `
      <div class="fixed bottom-4 left-4 right-4 bg-black/90 backdrop-blur-lg text-white p-4 rounded-xl shadow-2xl z-50 border border-white/20">
        <div class="flex items-start space-x-3">
          <div class="flex-shrink-0">
            <svg class="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
            </svg>
          </div>
          <div class="flex-1">
            <h3 class="font-semibold text-yellow-400 mb-1">Install Amalie AI</h3>
            <p class="text-sm text-white/80 mb-2">Add this app to your home screen for the best experience:</p>
            <ol class="text-xs text-white/70 space-y-1">
              <li>1. Tap the Share button <svg class="inline w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z"></path></svg></li>
              <li>2. Scroll down and tap "Add to Home Screen"</li>
              <li>3. Tap "Add" to install</li>
            </ol>
          </div>
          <button onclick="this.parentElement.parentElement.remove()" class="flex-shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors">
            <svg class="w-5 h-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>
    `;
    
    document.body.appendChild(iosPrompt);
    
    // Auto-hide after 15 seconds
    setTimeout(() => {
      const prompt = document.getElementById('ios-install-prompt');
      if (prompt) {
        prompt.style.opacity = '0';
        prompt.style.transform = 'translateY(100px)';
        prompt.style.transition = 'all 0.3s ease';
        setTimeout(() => prompt.remove(), 300);
      }
    }, 15000);
  }

  // Function to trigger the install prompt
  function installPWA() {
    if (!deferredPrompt) return;
    
    // Show the browser install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    deferredPrompt.userChoice.then((choiceResult: { outcome: string }) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      
      // Clear the saved prompt
      deferredPrompt = null;
      
      // Remove the install button
      const installButton = document.getElementById('install-button');
      if (installButton) {
        installButton.remove();
      }
    });
  }

  // Check if the app is already in standalone mode
  if (window.matchMedia('(display-mode: standalone)').matches) {
    console.log('App is already installed and running in standalone mode');
    return false;
  }

  return true;
}